import { graphql } from 'gatsby';
import { camelCase } from 'lodash-es';

import theme from '@klaviyo-mkt/gatsby-components/shared/styles/themes/default';
import GlobalHead from '../../layout/head';
import { ARTICLE_SCHEMA_PATHS, ArticleSchema } from '../../../components/json-ld/article';
import FAQsSchema from '@klaviyo-mkt/gatsby-components/organisms/accordion/faqs-schema';
import Layout from '../../layout';
import Default from './layout';

import type { DefaultTheme } from 'styled-components';
import type { HeadProps, PageProps } from 'gatsby';
import type { IHero, THeroBackgroundJpegs } from '@klaviyo-mkt/gatsby-components/organisms/hero/types';
import type { IDefaultData } from './layout';
import type { IPageContext } from '../types';
import type { TAllSitePagePageContext } from '../../../types';

const DARK_BACKGROUND_GRADIENTS = ['lakePoppy', 'lemonPoppy', 'sagePoppy', 'lavenderPoppy'];
const LIGHT_BACKGROUND_GRADIENTS = ['lightSandPoppy'];
const WHITE_BACKGROUND_COMPONENTS = [
  'BlogCardsComponent',
  'BlogHeroComponent',
  'TextTaglineComponent',
];

const setHeroColor = ({
  includesHero,
  hero,
  navigationColor = 'lightSand',
}: {
  hero: IHero;
  includesHero: boolean;
  navigationColor?: 'charcoal' | 'lightSand';
}) => (!includesHero ? navigationColor : hero?.background);

const DefaultTemplate = ({ data, pageContext, ...pageProps }: PageProps<IDefaultData, IPageContext>) => {
  const { typeNameDataKey } = pageContext;
  const templateData = data[typeNameDataKey];
  const includeHero = templateData?.includeHero ?? true;
  const includesWhiteBackgroundComponent = templateData?.components?.filter((row) => WHITE_BACKGROUND_COMPONENTS.includes(row?.componentType)).length !== 0;

  if (data?.[typeNameDataKey] === null) {
    return null;
  }

  if (
    templateData?.hero === null
  ) {
    return null;
  }

  // Is our background a jpg or color?
  const heroBackground = templateData.hero?.background;
  
  if (
    templateData.hero?.background &&
    DARK_BACKGROUND_GRADIENTS.includes(templateData.hero.background)
  ) {
    templateData.hero.backgroundJpeg = heroBackground as THeroBackgroundJpegs;
    templateData.hero.background = 'charcoal';
  }

  if (
    templateData.hero?.background &&
    LIGHT_BACKGROUND_GRADIENTS.includes(templateData.hero.background)
  ) {
    templateData.hero.backgroundJpeg = heroBackground as THeroBackgroundJpegs;
    templateData.hero.background = 'lightSand';
  }

  const heroColor = setHeroColor({
    includesHero: includeHero,
    hero: templateData.hero,
    navigationColor: templateData?.navigationColor,
  });

  return (
    <Layout
      {...pageProps}
      {...pageContext}
      path={pageProps.location.pathname}
      breadcrumbsBackground={includeHero ? 'transparent' : heroColor as 'charcoal' | 'lightSand' | 'transparent'}
      heroBackground={heroColor}
      navigationColor={templateData?.navigationColor}
      navigationStyle={templateData?.navigationStyle ?? 'default'}
      shortTitle={templateData?.shortTitle}
      templateName="Default"
      theme={Object.fromEntries(
        Object.entries(theme).map(([k, v]) => {
          const color = includesWhiteBackgroundComponent ? theme.colors.white : theme.colors.lightSand;
          if (v?.backgroundColor?.light) {
            v.backgroundColor.light = color;
          }
          if (v?.light?.background) {
            v.light.background = color;
          }
          if (v?.light?.backgroundColor) {
            v.light.backgroundColor = color;
          }
          if (v?.light?.default?.background) {
            v.light.default.background = color;
          }
          if (v?.mainBackground?.light) {
            v.mainBackground.light = color;
          }
          return [k, v];
        }),
      ) as DefaultTheme}
      translations={templateData.translations}
    >
      <Default
        components={templateData?.components || []}
        hero={templateData.hero}
        includeHero={includeHero}
        modals={data?.defaultPage?.modals}
      />
    </Layout>
  );
};

export const query = graphql`
  query DefaultTemplate(
    $id: String!,
    $cptFrontPage: Boolean = false,
    $defaultPage: Boolean = false,
  ) {
    cptFrontPage(id: {eq: $id}) @include(if: $cptFrontPage) {
      ...CptFrontPageDetails
    }
    defaultPage(id: {eq: $id}) @include(if: $defaultPage) {
      ...DefaultPageDetails
    }
    allPageHero(filter: {parent: {id: {eq: $id}}}) {
      nodes {
        internal {
          contentDigest
        }
      }
    }
    allPageComponent(filter: {parent: {id: {eq: $id}}}) {
      nodes {
        internal {
          contentDigest
        }
      }
    }
  }
`;

export const Head = ({
  data,
  location,
  pageContext,
}: HeadProps<IDefaultData, TAllSitePagePageContext>) => {
  const { typeNameDataKey } = pageContext;
  const seoData = data?.[typeNameDataKey]?.headlessSeo;
  
  return (
    <GlobalHead
      isFrontPage={pageContext?.isFrontPage}
      pathname={location.pathname}
      seo={seoData}
      translations={data?.[typeNameDataKey]?.translations}
    >
      {ARTICLE_SCHEMA_PATHS.some((schemaPath) => location.pathname.includes(schemaPath)) ? (
        <ArticleSchema
          dateModified={seoData?.opengraphModifiedTime}
          datePublished={seoData?.opengraphPublishedTime}
          description={seoData?.metaDesc}
          disambiguatingDescription={seoData?.metaDesc}
          headline={seoData?.title}
          image={seoData?.opengraphImage?.publicUrl}
          url={seoData?.canonical}
        />
      ) : null}
      {seoData?.jsonLd ? (
        <script type="application/ld+json">{seoData?.jsonLd}</script>
      ) : null}
      <FAQsSchema components={data?.[typeNameDataKey]?.components || []} />
    </GlobalHead>
  );
};

export default DefaultTemplate;
